// Story 2.3 — shared release form used by:
//   * The composer wizard's Step 4 + Step 5 (mode="wizard-step").
//   * The detail-page Section 2 "Release as drop" panel (mode="detail-panel").
//
// Parent owns the state container (so the wizard can persist across
// step transitions); this component is a controlled form that renders
// kind-aware inputs + the release-now Switch + the bulletin selector.
//
// Constants mirror server-side `drop_service` for client-side
// validation. The server is the source of truth; client-side validation
// is a UX nicety to disable the submit button when the form is
// obviously invalid.

import { Link } from "react-router-dom";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Switch,
} from "@greenhold/components";

import { useAdminBulletinPostsForDropPicker } from "./useAdminBulletinPostsForDropPicker";

export const MIN_PRICE_CENTS = 100;
export const MAX_PRICE_CENTS = 100_000;
export const MIN_PER_USER_CAP = 1;
export const MAX_PER_USER_CAP = 1000;
export const REGULAR_DEFAULT_RETIREMENT_DAYS = 180;

export type DropKind = "regular" | "special" | "welcome";

export type DropReleaseFormState = {
  price_cents_input: string; // raw string so the user can type freely
  release_now: boolean;
  scheduled_at_input: string; // datetime-local format
  retiring_at_input: string; // datetime-local format
  per_user_cap_input: string;
  link_bulletin: boolean;
  linked_bulletin_post_id: string | null;
};

export const EMPTY_RELEASE_FORM_STATE: DropReleaseFormState = {
  price_cents_input: "750",
  release_now: false,
  scheduled_at_input: "",
  retiring_at_input: "",
  per_user_cap_input: "5",
  link_bulletin: false,
  linked_bulletin_post_id: null,
};

export function formatEur(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

export function parseDatetimeLocalToIso(s: string): string | null {
  if (!s) return null;
  // <input type="datetime-local"> emits "YYYY-MM-DDTHH:mm" with no
  // timezone; treat as local time + convert to ISO with offset.
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function isFormValid(
  state: DropReleaseFormState,
  portfolioKind: DropKind,
): boolean {
  // Welcome: price + cap + retiring_at are all server-enforced
  // (price=0, no cap, no retiring_at). The SPA still validates
  // scheduled_at + bulletin-link presence.
  if (portfolioKind !== "welcome") {
    const price = parseInt(state.price_cents_input, 10);
    if (!Number.isFinite(price) || price < MIN_PRICE_CENTS || price > MAX_PRICE_CENTS) {
      return false;
    }
  }
  if (!state.release_now) {
    const scheduled = parseDatetimeLocalToIso(state.scheduled_at_input);
    if (!scheduled) return false;
  }
  if (portfolioKind === "special") {
    const cap = parseInt(state.per_user_cap_input, 10);
    if (!Number.isFinite(cap) || cap < MIN_PER_USER_CAP || cap > MAX_PER_USER_CAP) {
      return false;
    }
    // Special drops REQUIRE explicit retiring_at.
    if (!state.retiring_at_input) return false;
    const retiring = parseDatetimeLocalToIso(state.retiring_at_input);
    if (!retiring) return false;
  } else if (portfolioKind === "regular") {
    // Regular: retiring_at is optional. If provided, validate it parses.
    if (state.retiring_at_input) {
      const retiring = parseDatetimeLocalToIso(state.retiring_at_input);
      if (!retiring) return false;
    }
  }
  // Welcome: no retiring_at validation — server forbids it.
  if (state.link_bulletin && !state.linked_bulletin_post_id) {
    return false;
  }
  return true;
}

export type DropReleaseFormProps = {
  portfolioKind: DropKind;
  mode: "wizard-step" | "detail-panel";
  state: DropReleaseFormState;
  setState: (next: DropReleaseFormState) => void;
};

export function DropReleaseForm({
  portfolioKind,
  mode: _mode,
  state,
  setState,
}: DropReleaseFormProps) {
  const bulletinQuery = useAdminBulletinPostsForDropPicker();

  function patch(p: Partial<DropReleaseFormState>) {
    setState({ ...state, ...p });
  }

  const priceParsed = parseInt(state.price_cents_input, 10);
  const priceValid =
    Number.isFinite(priceParsed) &&
    priceParsed >= MIN_PRICE_CENTS &&
    priceParsed <= MAX_PRICE_CENTS;

  return (
    <div className="space-y-6">
      {/* Kind summary card */}
      <Card>
        <CardHeader>
          <CardTitle>
            {portfolioKind === "regular"
              ? "Regular dossier"
              : portfolioKind === "special"
                ? "Special dossier"
                : "Welcome dossier"}
          </CardTitle>
          <CardDescription>
            {portfolioKind === "regular"
              ? "Uncapped purchases; ~6-month shop life by default (tunable below)."
              : portfolioKind === "special"
                ? "Per-user purchase cap; admin-chosen retirement window."
                : "Free, perpetual; granted to every newly-verified user. Activate via /admin/welcome-portfolio after release."}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Price (hidden for welcome — server forces 0) */}
      {portfolioKind === "welcome" ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm">
              <strong>Welcome portfolios are free (€0.00).</strong>{" "}
              Server-enforced.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="price_cents">Price (EUR cents)</Label>
          <Input
            id="price_cents"
            type="number"
            min={MIN_PRICE_CENTS}
            max={MAX_PRICE_CENTS}
            value={state.price_cents_input}
            onChange={(e) => patch({ price_cents_input: e.target.value })}
            aria-describedby="price_cents_hint"
          />
          <p id="price_cents_hint" className="text-xs opacity-70">
            {priceValid
              ? `= ${formatEur(priceParsed)}`
              : `Must be between ${MIN_PRICE_CENTS} (${formatEur(MIN_PRICE_CENTS)}) and ${MAX_PRICE_CENTS} (${formatEur(MAX_PRICE_CENTS)}) cents.`}
          </p>
        </div>
      )}

      {/* Release-now toggle */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Switch
            id="release_now"
            checked={state.release_now}
            onCheckedChange={(checked) =>
              patch({ release_now: Boolean(checked) })
            }
            aria-label="Release the drop immediately on publish"
          />
          <Label htmlFor="release_now" className="cursor-pointer">
            Release now
          </Label>
        </div>
        <p className="text-xs opacity-70 ml-14">
          {state.release_now
            ? "Drop goes live immediately when you click Publish."
            : "Pick a future scheduled time below; the Story 2.6 worker will transition status='live' at that time."}
        </p>
      </div>

      {/* scheduled_at (hidden when release_now) */}
      {!state.release_now ? (
        <div className="space-y-2">
          <Label htmlFor="scheduled_at">Scheduled release time</Label>
          <Input
            id="scheduled_at"
            type="datetime-local"
            value={state.scheduled_at_input}
            onChange={(e) =>
              patch({ scheduled_at_input: e.target.value })
            }
          />
        </div>
      ) : null}

      {/* per_user_cap (special only) */}
      {portfolioKind === "special" ? (
        <div className="space-y-2">
          <Label htmlFor="per_user_cap">Per-user purchase cap</Label>
          <Input
            id="per_user_cap"
            type="number"
            min={MIN_PER_USER_CAP}
            max={MAX_PER_USER_CAP}
            value={state.per_user_cap_input}
            onChange={(e) =>
              patch({ per_user_cap_input: e.target.value })
            }
            aria-describedby="per_user_cap_hint"
          />
          <p id="per_user_cap_hint" className="text-xs opacity-70">
            Each user can buy at most this many dossiers from this drop.
            ({MIN_PER_USER_CAP}-{MAX_PER_USER_CAP})
          </p>
        </div>
      ) : null}

      {/* retiring_at (hidden for welcome — server forbids it) */}
      {portfolioKind === "welcome" ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm">
              <strong>Welcome portfolios are perpetual.</strong>{" "}
              Server-enforced (no retirement timestamp).
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="retiring_at">
            Retirement time{" "}
            {portfolioKind === "regular" ? (
              <span className="text-xs opacity-60 font-normal">
                (optional — defaults to scheduled + {REGULAR_DEFAULT_RETIREMENT_DAYS} days)
              </span>
            ) : (
              <span className="text-xs opacity-60 font-normal">(required)</span>
            )}
          </Label>
          <Input
            id="retiring_at"
            type="datetime-local"
            value={state.retiring_at_input}
            onChange={(e) => patch({ retiring_at_input: e.target.value })}
          />
        </div>
      )}

      {/* Bulletin link */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Bulletin announcement</CardTitle>
          <CardDescription>
            Optionally link a published Bulletin post so the public
            dossier page (Story 2.8) and Bulletin index (Story 2.9) can
            cross-link.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              id="link_bulletin"
              checked={state.link_bulletin}
              onCheckedChange={(checked) =>
                patch({
                  link_bulletin: Boolean(checked),
                  linked_bulletin_post_id: checked
                    ? state.linked_bulletin_post_id
                    : null,
                })
              }
              aria-label="Link a Bulletin announcement"
            />
            <Label htmlFor="link_bulletin" className="cursor-pointer">
              Link a Bulletin announcement
            </Label>
          </div>

          {state.link_bulletin ? (
            <BulletinPicker
              query={bulletinQuery}
              value={state.linked_bulletin_post_id}
              onChange={(id) => patch({ linked_bulletin_post_id: id })}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

type BulletinPickerProps = {
  query: ReturnType<typeof useAdminBulletinPostsForDropPicker>;
  value: string | null;
  onChange: (id: string | null) => void;
};

function BulletinPicker({ query, value, onChange }: BulletinPickerProps) {
  if (query.isPending) {
    return <p className="text-sm opacity-70">Loading bulletin posts…</p>;
  }
  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t load bulletin posts: {query.error.message}
      </p>
    );
  }
  const posts = query.data ?? [];
  if (posts.length === 0) {
    return (
      <div className="space-y-2">
        <Badge variant="secondary">No unlinked published posts</Badge>
        <p className="text-sm opacity-80">
          Either no Bulletin posts have been published yet OR every
          published post is already linked to another drop.{" "}
          <Link to="/admin/bulletin/new" className="underline decoration-dotted">
            Compose a new Bulletin →
          </Link>
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Label htmlFor="bulletin_picker">Pick a published Bulletin post</Label>
      <select
        id="bulletin_picker"
        className="block w-full rounded-md border border-brand-forest-200 bg-white p-2 text-sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        aria-label="Select a published Bulletin announcement to link to this drop"
      >
        <option value="">— Select a post —</option>
        {posts.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title} ({p.slug})
          </option>
        ))}
      </select>
    </div>
  );
}
